// ABOUTME: Manual server selection between the app./eu./ru. entry points (stable RF access).
// ABOUTME: Cookie-persisted choice, set via ?server=; the switcher UI itself lives in the native app.

// There is deliberately NO web UI for this: the switcher lives in the native
// Apple/Google app's own interface, which works even when the front fails to
// load at all. The web side only provides the mechanism — a `?server=auto|eu|ru`
// URL parameter that persists the choice, and a boot redirect off the Auto
// host. That same parameter doubles as the support tool for browser users.

export type ServerChoice = "auto" | "eu" | "ru"

// The three production entry points (docs/MULTI_ORIGIN_SPEC.md): `app.` is Auto
// (geo-DNS picks the path), `eu.` goes straight to GKE, `ru.` goes through the
// RF proxy box. All are subdomains of `.com`, so the Kratos session cookie
// (Domain=.trainingendurance.com) survives switching — no re-login needed.
export const ENTRY_HOSTS: Record<ServerChoice, string> = {
  auto: "app.trainingendurance.com",
  eu: "eu.trainingendurance.com",
  ru: "ru.trainingendurance.com",
}

const COOKIE_NAME = "te_server"
const COOKIE_MAX_AGE_S = 365 * 24 * 60 * 60

const isServerChoice = (value: unknown): value is ServerChoice => value === "auto" || value === "eu" || value === "ru"

/** Entry point the app is currently served from, or null outside the three production hosts. */
export const currentServer = (hostname: string = window.location.hostname): ServerChoice | null => {
  const host = hostname.replace(/\.$/, "")
  const entries = Object.keys(ENTRY_HOSTS) as ServerChoice[]
  return entries.find((choice) => ENTRY_HOSTS[choice] === host) ?? null
}

/** Selection only exists on the production entry hosts — staging/localhost have no eu./ru. twins. */
export const isServerSelectAvailable = (hostname: string = window.location.hostname): boolean =>
  currentServer(hostname) !== null

export const readServerChoice = (): ServerChoice | null => {
  const pair = document.cookie.split("; ").find((part) => part.startsWith(`${COOKIE_NAME}=`))
  const value = pair?.slice(COOKIE_NAME.length + 1)
  return isServerChoice(value) ? value : null
}

export const writeServerChoice = (choice: ServerChoice): void => {
  // Domain=.trainingendurance.com shares the choice across all three entry
  // hosts. That is load-bearing: with per-origin storage the `app.` host could
  // keep redirecting to `ru.` while only `ru.`'s storage is reachable to undo
  // it. Off the production domain (tests, localhost) a host-only cookie is set.
  const domain = window.location.hostname.endsWith(".trainingendurance.com") ? "; Domain=.trainingendurance.com" : ""
  const secure = window.location.protocol === "https:" ? "; Secure" : ""
  document.cookie = `${COOKIE_NAME}=${choice}; Max-Age=${COOKIE_MAX_AGE_S}; Path=/${domain}${secure}; SameSite=Lax`
}

/**
 * Boot-time hook (before anything else initializes): honors a persisted eu./ru.
 * choice by redirecting off the Auto host, preserving path, query and hash.
 * Returns true when a redirect was issued.
 *
 * Only the Auto host ever redirects — `eu.`/`ru.` in the address bar IS an
 * explicit choice, and a one-directional redirect cannot loop. `?server=auto`
 * on `app.` is the escape hatch when a chosen server is unreachable; the
 * parameter also accepts eu/ru and always overrides the cookie.
 */
export const applyServerChoiceRedirect = (): boolean => {
  if (!isServerSelectAvailable()) return false

  const params = new URLSearchParams(window.location.search)
  const forced = params.get("server")
  params.delete("server")
  const cleanedQuery = params.toString()
  const cleanedSearch = cleanedQuery ? `?${cleanedQuery}` : ""

  if (isServerChoice(forced)) {
    writeServerChoice(forced)
    window.history.replaceState(null, "", window.location.pathname + cleanedSearch + window.location.hash)
  }

  if (currentServer() !== "auto") return false
  // Offline (PWA cold start): redirecting would trade the cached shell for a browser error page.
  if (!navigator.onLine) return false

  const choice = isServerChoice(forced) ? forced : readServerChoice()
  if (choice !== "eu" && choice !== "ru") return false

  const { pathname, hash } = window.location
  window.location.replace(`https://${ENTRY_HOSTS[choice]}${pathname}${cleanedSearch}${hash}`)
  return true
}
